import { Button } from '@shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/ui/dialog'
import { Separator } from '@shared/ui/separator'

interface Props {
  openHowWorks: boolean
  setOpenHowWorks: (openHowWorks: boolean) => void
}

function HowValidationWorksDialog({ openHowWorks, setOpenHowWorks }: Props) {
  return (
    <Dialog open={openHowWorks} onOpenChange={setOpenHowWorks}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>📋 Validation Workflow Overview</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm text-muted-foreground">
          <div>
            <p className="text-foreground">
              This is your central hub for validating and managing meter readings collected during the cycle.
              Readings flow through different tabs based on their status, and you can take appropriate actions to ensure data quality before billing.
            </p>
          </div>

          <div className="rounded-md border p-3 bg-muted/30">
            <div className="text-xs font-medium text-foreground mb-1">Quick Workflow</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>Start with Pending (V1) — Review and validate newly collected readings</li>
              <li>Check Exemptions (V2) — Handle readings flagged by validation rules or marked as RCNT/Faulty</li>
              <li>Monitor Missing — Track uncollected readings and take action if deadlines approach</li>
              <li>Review Revisit — Assign flagged readings back to field staff for re-collection</li>
              <li>Handle Duplicate — Resolve multiple readings submitted for the same meter</li>
              <li>
                Process Ghost Meters — Identify readings that appear without prior linkage or history; verify authenticity and resolve mismatches before proceeding
              </li>
              <li>Verify Pass — Review validated readings ready for billing (undo if needed)</li>
            </ul>
          </div>

          <div className="rounded-md border p-3">
            <div className="text-xs font-medium text-foreground mb-1">💡 Pro Tips</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use status badges and counts to prioritize your work</li>
              <li>Hover over info icons (ⓘ) for detailed explanations</li>
              <li>Use search to quickly find specific meters</li>
              <li>Actions are reversible — use the Undo button if you make a mistake</li>
            </ul>
          </div>

          <Separator />

          <div className="space-y-3">
            <div className="text-sm font-semibold text-foreground">TAB-SPECIFIC GUIDANCE</div>

            {/* 1. Pending Tab */}
            <div className="rounded-md border p-3">
              <div className="font-medium text-foreground mb-1">1. PENDING (V1) TAB</div>
              <div className="mb-2">
                <span className="font-medium text-foreground">What it means: </span>
                <span>Newly collected readings awaiting your validation. Submitted by field staff with photos and queued for review.</span>
              </div>
              <div className="mb-2">
                <span className="font-medium text-foreground">How to use it: </span>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Review reasonability vs previous consumption</li>
                  <li>Verify photo quality and meter visibility</li>
                  <li>
                    Take action:
                    <ul className="list-disc pl-5 mt-1">
                      <li>✅ Pass Reading — Accurate and verified (moves to Pass)</li>
                      <li>⚠️ Exempt Reading — Needs supervisor review (moves to Exemptions)</li>
                      <li>🔄 Send for Revisit — Incorrect/unclear (moves to Revisit)</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground">
                Tip: Sort by anomalies, use filters, and target 95% validation within 24 hours.
              </div>
            </div>

            {/* 2. Exemptions Tab */}
            <div className="rounded-md border p-3">
              <div className="font-medium text-foreground mb-1">2. EXEMPTIONS (V2) TAB</div>
              <div className="mb-2">
                <span className="font-medium text-foreground">What it means: </span>
                <span>
                  Readings requiring special attention: auto-flagged (spikes/negatives), RCNT, faulty, or manually exempted.
                </span>
              </div>
              <div className="mb-2">
                <span className="font-medium text-foreground">How to use it: </span>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Review flagged reason and context</li>
                  <li>
                    Take action:
                    <ul className="list-disc pl-5 mt-1">
                      <li>✅ Pass — Acceptable after investigation</li>
                      <li>🔄 Revisit — Needs field verification</li>
                      <li>🔧 Adjust — Apply correction/estimation where appropriate</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground">
                Tip: Prioritize RCNT/faulty; spot patterns by route; contact field staff when unclear.
              </div>
            </div>

            {/* 3. Missing Tab */}
            <div className="rounded-md border p-3">
              <div className="font-medium text-foreground mb-1">3. MISSING TAB</div>
              <div className="mb-2">
                <span className="font-medium text-foreground">What it means: </span>
                <span>Meters without a reading yet in this cycle.</span>
              </div>
              <div className="mb-2">
                <span className="font-medium text-foreground">How to use it: </span>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Monitor deadlines and field progress</li>
                  <li>
                    When near due:
                    <ul className="list-disc pl-5 mt-1">
                      <li>📤 Upload manually if reading received outside system</li>
                      <li>🤖 Estimate using rules (when revisit not feasible)</li>
                      <li>📞 Remind field staff</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground">
                Tip: Set alerts for last 3 days; prioritize commercial meters; document estimation reasons.
              </div>
            </div>

            {/* 4. Revisit Tab */}
            <div className="rounded-md border p-3">
              <div className="font-medium text-foreground mb-1">4. REVISIT TAB</div>
              <div className="mb-2">
                <span className="font-medium text-foreground">What it means: </span>
                <span>Readings sent back for re-collection due to issues or unclear photos.</span>
              </div>
              <div className="mb-2">
                <span className="font-medium text-foreground">How to use it: </span>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Review reason and assign to field staff</li>
                  <li>Set priority and track re-submissions</li>
                  <li>Undo if sent by mistake</li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground">
                Tip: Group by area for efficient routing; add clear notes; set realistic timeframes and follow up.
              </div>
            </div>

            {/* 5. Ghost Meters Tab */}
            <div className="rounded-md border p-3">
              <div className="font-medium text-foreground mb-1">5. GHOST METER TAB</div>
              <div className="mb-2">
                <span className="font-medium text-foreground">What it means: </span>
                <span>Readings from meters not in this cycle (new install, wrong ID, or recently added).</span>
              </div>
              <div className="mb-2">
                <span className="font-medium text-foreground">How to use it: </span>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Investigate meter status</li>
                  <li>
                    Decide:
                    <ul className="list-disc pl-5 mt-1">
                      <li>➕ Add to System</li>
                      <li>🗑️ Uninstall</li>
                      <li>🔀 Move to This Route</li>
                      <li>❌ Reject</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground">
                Tip: Verify location and details; coordinate with field staff; document decisions.
              </div>
            </div>

            {/* 6. Pass Tab */}
            <div className="rounded-md border p-3">
              <div className="font-medium text-foreground mb-1">6. PASS TAB</div>
              <div className="mb-2">
                <span className="font-medium text-foreground">What it means: </span>
                <span>Validated readings ready for billing.</span>
              </div>
              <div className="mb-2">
                <span className="font-medium text-foreground">How to use it: </span>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Final review and quality checks</li>
                  <li>Export reports</li>
                  <li>Undo if validation was mistaken</li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground">
                Tip: Aim for 95%+ in Pass by cycle end; lock only when confident in accuracy.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpenHowWorks(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default HowValidationWorksDialog
